import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  FileText,
  Receipt,
  ScrollText,
  UserCog,
  LogOut,
  Moon,
  Sun,
  BarChart3,
  Package,
  LifeBuoy,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getInitials, getAvatarColor } from '../../lib/utils';
import { useDarkMode } from '../../hooks/useDarkMode';

export const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/contacts', icon: Users, label: 'Customers' },
  { to: '/opportunities', icon: TrendingUp, label: 'Pipeline' },
  { to: '/products', icon: Package, label: 'Produits' },
  { to: '/tickets', icon: LifeBuoy, label: 'Support' },
  { to: '/quotes', icon: FileText, label: 'Devis' },
  { to: '/invoices', icon: Receipt, label: 'Factures' },
];

export const adminItems = [
  { to: '/users', icon: UserCog, label: 'Utilisateurs' },
  { to: '/audit-logs', icon: ScrollText, label: 'Audit de logs' },
];

export function Sidebar() {
  const { user, logout, isAdmin } = useAuth();
  const { isDark, toggle: toggleDarkMode } = useDarkMode();

  return (
    <aside className="sidebar fixed left-0 top-0 bottom-0 z-40 flex flex-col w-60">
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <BarChart3 size={18} className="text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-none truncate" style={{ color: 'hsl(var(--sidebar-fg))' }}>
            SalePulse
          </p>
          <p className="text-xs mt-0.5 truncate" style={{ color: 'hsl(var(--sidebar-muted))' }}>
            Gestion commerciale
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--sidebar-muted))' }}>
          Navigation
        </p>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn('sidebar-item', isActive && 'active')
            }
            data-testid={`nav-${item.to.slice(1)}`}
          >
            <item.icon size={18} className="flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--sidebar-muted))' }}>
              Administration
            </p>
            {adminItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn('sidebar-item', isActive && 'active')
                }
                data-testid={`nav-${item.to.slice(1)}`}
              >
                <item.icon size={18} className="flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Dark mode toggle */}
      <div className="p-3" style={{ borderTop: '1px solid hsl(var(--sidebar-border))' }}>
        <button
          onClick={toggleDarkMode}
          className="sidebar-item w-full"
          title={isDark ? 'Mode clair' : 'Mode sombre'}
        >
          {isDark ? <Sun size={18} className="flex-shrink-0" /> : <Moon size={18} className="flex-shrink-0" />}
          <span>{isDark ? 'Mode clair' : 'Mode sombre'}</span>
        </button>
      </div>

      {/* User section */}
      <div className="p-3" style={{ borderTop: '1px solid hsl(var(--sidebar-border))' }}>
        <div className="flex items-center gap-2.5 px-2 py-1.5">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback className={cn('text-xs font-semibold', getAvatarColor(user?.name))}>
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate leading-none" style={{ color: 'hsl(var(--sidebar-fg))' }}>
              {user?.name}
            </p>
            <p className="text-xs truncate mt-0.5" style={{ color: 'hsl(var(--sidebar-muted))' }}>
              {user?.email}
            </p>
          </div>
          <button
            onClick={logout}
            className="flex-shrink-0 p-1.5 rounded-md transition-colors"
            style={{ color: 'hsl(var(--sidebar-muted))' }}
            onMouseEnter={e => e.currentTarget.style.color = 'hsl(var(--sidebar-fg))'}
            onMouseLeave={e => e.currentTarget.style.color = 'hsl(var(--sidebar-muted))'}
            title="Se déconnecter"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
